import React, { useCallback, useState } from 'react';
import {
  BackgroundOption,
  TShirtBuilder,
  ViewImages
} from 'react-tshirt-builder';

import styled from 'styled-components';

import { Color } from '@assets/constants';
import { icons } from '@assets/icons';

type ProductType = 'tshirt' | 'pillow';

const tshirtBackgroundOptions: BackgroundOption[] = [
  {
    id: 'white',
    name: 'Бял',
    color: '#FFFFFF',
    frontImage: require('../assets/images/builder/white_front.png'),
    backImage: require('../assets/images/builder/white_back.png')
  },
  {
    id: 'black',
    name: 'Черен',
    color: '#1a1a1a',
    frontImage: require('../assets/images/builder/black_front.png'),
    backImage: require('../assets/images/builder/black_back.png')
  },
  {
    id: 'gray',
    name: 'Сив',
    color: '#808080',
    frontImage: require('../assets/images/builder/gray_front.png'),
    backImage: require('../assets/images/builder/gray_back.png')
  },
  {
    id: 'red',
    name: 'Червен',
    color: '#e53935',
    frontImage: require('../assets/images/builder/red_front.png'),
    backImage: require('../assets/images/builder/red_back.png')
  },
  {
    id: 'blue',
    name: 'Син',
    color: '#1e88e5',
    frontImage: require('../assets/images/builder/blue_front.png'),
    backImage: require('../assets/images/builder/blue_back.png')
  },
  {
    id: 'darkblue',
    name: 'Тъмносин',
    color: '#1a237e',
    frontImage: require('../assets/images/builder/darkblue_front.png'),
    backImage: require('../assets/images/builder/darkblue_back.png')
  },
  {
    id: 'green',
    name: 'Зелен',
    color: '#43a047',
    frontImage: require('../assets/images/builder/green_front.png'),
    backImage: require('../assets/images/builder/green_back.png')
  },
  {
    id: 'yellow',
    name: 'Жълт',
    color: '#fdd835',
    frontImage: require('../assets/images/builder/yellow_front.png'),
    backImage: require('../assets/images/builder/yellow_back.png')
  }
];

const pillowBackgroundOptions: BackgroundOption[] = [
  {
    id: 'pillow',
    name: 'Възглавница',
    color: '#FFFFFF',
    frontImage: require('../assets/images/builder/pillow.png'),
    backImage: require('../assets/images/builder/pillow.png')
  }
];

// Static config objects - defined outside component to maintain referential equality
const TSHIRT_BUILDER_CONFIG = {
  width: 700,
  height: 800,
  printableArea: {
    minX: 190,
    minY: 120,
    maxX: 510,
    maxY: 700
  },
  allowRotation: true,
  minImageSize: 30,
  exportScale: 1
} as const;

const PILLOW_BUILDER_CONFIG = {
  width: 700,
  height: 800,
  printableArea: {
    minX: 90,
    minY: 130,
    maxX: 600,
    maxY: 640
  },
  allowRotation: true,
  minImageSize: 30,
  exportScale: 1
} as const;

const RESPONSIVE_CONFIG = {
  enabled: true
} as const;

const TShirtBuilderPageComponent = () => {
  const [selectedProductType, setSelectedProductType] =
    useState<ProductType | null>(null);
  const [, setImages] = useState<ViewImages>({ front: [], back: [] });
  const [exportedImages, setExportedImages] = useState<{
    front: string;
    back: string;
  } | null>(null);

  const handleChange = useCallback((newImages: ViewImages) => {
    setImages(newImages);
  }, []);

  const handleExport = useCallback(
    (images: { front: string; back: string }) => {
      setExportedImages(images);
    },
    []
  );

  const handleSelectProductType = useCallback((type: ProductType) => {
    setSelectedProductType(type);
    setExportedImages(null);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedProductType(null);
    setExportedImages(null);
  }, []);

  if (!selectedProductType) {
    return (
      <Wrapper>
        <Row>
          <Title>Избери продукт</Title>
          <icons.FaEdit size={26} color={Color.DARK_GRAY} />
        </Row>
        <ProductTypeSelector>
          <ProductTypeCard onClick={() => handleSelectProductType('tshirt')}>
            <ProductTypeImage
              src={require('../assets/images/builder/white_front.png')}
              alt="Тениска"
            />
            <ProductTypeLabel>Създай тениска</ProductTypeLabel>
          </ProductTypeCard>
          <ProductTypeCard onClick={() => handleSelectProductType('pillow')}>
            <ProductTypeImage
              src={require('../assets/images/builder/pillow.png')}
              alt="Възглавница"
            />
            <ProductTypeLabel>Създай възглавница</ProductTypeLabel>
          </ProductTypeCard>
        </ProductTypeSelector>
      </Wrapper>
    );
  }

  const isPillow = selectedProductType === 'pillow';
  const backgroundOptions = isPillow
    ? pillowBackgroundOptions
    : tshirtBackgroundOptions;
  const builderConfig = isPillow
    ? PILLOW_BUILDER_CONFIG
    : TSHIRT_BUILDER_CONFIG;
  const title = isPillow ? 'Създай възглавница' : 'Създай тениска';

  return (
    <Wrapper>
      <Row>
        <BackButton onClick={handleBack}>
          <icons.FaArrowLeft size={20} color={Color.DARK_GRAY} />
        </BackButton>
        <Title>{title}</Title>
      </Row>
      <Content>
        <TShirtBuilder
          key={selectedProductType}
          backgrounds={backgroundOptions}
          initialBackgroundId={isPillow ? 'pillow' : 'white'}
          config={builderConfig}
          responsive={RESPONSIVE_CONFIG}
          onChange={handleChange}
          onExport={handleExport}
        />
        {exportedImages && (
          <ExportedSection>
            <ExportedImagesContainer>
              <ExportedImageWrapper>
                <ImageLabel>Предна страна</ImageLabel>
                <ExportedImage src={exportedImages.front} alt="Front view" />
              </ExportedImageWrapper>
              <ExportedImageWrapper>
                <ImageLabel>Задна страна</ImageLabel>
                <ExportedImage src={exportedImages.back} alt="Back view" />
              </ExportedImageWrapper>
            </ExportedImagesContainer>
          </ExportedSection>
        )}
      </Content>
    </Wrapper>
  );
};

// Wrap with React.memo to prevent re-renders from parent state changes
export const TShirtBuilderPage = React.memo(TShirtBuilderPageComponent);

const Wrapper = styled.div``;

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 1.5rem;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: ${Color.DARK_GRAY};
  font-weight: bold;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 2rem;
`;

const ExportedSection = styled.div`
  margin-top: 2rem;
  width: 100%;
  max-width: 700px;
`;

const ExportedImagesContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
`;

const ExportedImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 300px;
`;

const ImageLabel = styled.span`
  font-size: 0.875rem;
  color: ${Color.DARK_GRAY};
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ExportedImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 7 / 8;
  object-fit: contain;
  border: 1px solid ${Color.LIGHT_GRAY};
  border-radius: 8px;
  background-color: #fafafa;
`;

const ProductTypeSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 2rem;
  flex-wrap: wrap;
`;

const ProductTypeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border: 2px solid ${Color.LIGHT_GRAY};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Color.DARK_GRAY};
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ProductTypeImage = styled.img`
  width: 200px;
  height: 200px;
  object-fit: contain;
  margin-bottom: 1rem;
`;

const ProductTypeLabel = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${Color.DARK_GRAY};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${Color.LIGHT_GRAY};
  }
`;
